import React, { ReactElement, useEffect, useRef, useState } from 'react';

interface ImageComponentProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  quality?: number;
  width?: number;
  height?: number;
  format?: 'jpeg' | 'webp' | 'png' | 'jpg';
  classNames?: string;
  loading?: boolean;
  customLoader?: ReactElement;
}

export const InoImage: React.FC<ImageComponentProps> = ({
  src,
  alt,
  fallbackSrc = 'fallback.jpg',
  quality = 0.7,
  width = 300,
  height = 300,
  format = 'jpeg',
  loading = false,
  customLoader,
  classNames = '',
}) => {
  const [displaySrc, setDisplaySrc] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [boxSize, setBoxSize] = useState<{ width: number; height: number }>({
    width,
    height,
  });

  const updateBoxSize = () => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setBoxSize({ width, height });
    }
  };

  useEffect(() => {
    updateBoxSize();
    window.addEventListener('resize', updateBoxSize);
    return () => {
      window.removeEventListener('resize', updateBoxSize);
    };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    img.onload = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = boxSize.width;
        canvas.height = boxSize.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, boxSize.width, boxSize.height);

          const compressedImageUrl = canvas.toDataURL(format, quality);
          setDisplaySrc(compressedImageUrl);
          sessionStorage.setItem(`imageCache_${src}`, compressedImageUrl);
          setIsLoaded(true);
          setIsLoading(false);
        }
      }
    };

    img.onerror = () => {
      setDisplaySrc(fallbackSrc);
      setHasError(true);
      setIsLoading(false);
    };

    return () => {
      setIsLoaded(false);
      setIsLoading(true);
      setDisplaySrc('');
    };
  }, [src, boxSize, quality, fallbackSrc, format]);

  return (
    <div
      ref={containerRef}
      style={{
        width: boxSize.width + 'px',
        height: boxSize.height + 'px',
        overflow: 'hidden',
        position: 'relative',
      }}
      className={classNames}
    >
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {isLoading && !hasError && loading && (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {customLoader ? customLoader : <p>loading...</p>}
        </div>
      )}
      {displaySrc && (
        <img
          src={displaySrc}
          alt={alt}
          width={boxSize.width}
          height={boxSize.height}
          style={{
            objectFit: 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
};
